import React, { useState } from 'react';
import { LuChevronDown, LuChevronUp } from 'react-icons/lu';

export interface GroupedListGroup<TItem = any, TGroup = any> {
    /** Unique identifier for the group */
    id: string;
    /** The group data object */
    group: TGroup;
    /** Items belonging to this group */
    items: TItem[];
    /** Whether the group should be initially expanded */
    defaultExpanded?: boolean;
}

export interface GroupedListColumn<TItem = any> {
    /** Unique key for the column */
    key: string;
    /** Column header label */
    header: string;
    /** Width of the column (e.g., '250px', '150px', or 'flex-1' for flexible width) */
    width?: string;
    /** Custom render function for the cell */
    render?: (item: TItem) => React.ReactNode;
    /** Accessor function to get the value from the item */
    accessor?: (item: TItem) => any;
    /** Optional className for the column */
    className?: string;
}

export interface GroupedListProps<TItem = any, TGroup = any> {
    /** Array of groups with their items */
    groups: GroupedListGroup<TItem, TGroup>[];
    /** Column definitions for the data rows */
    columns: GroupedListColumn<TItem>[];
    /** Custom render function for the group header content (left side) */
    renderGroupHeader: (group: TGroup) => React.ReactNode;
    /** Optional custom render function for the group header actions (right side) */
    renderGroupActions?: (group: TGroup) => React.ReactNode;
    /** Optional className for the container */
    className?: string;
    /** Optional className for the header row */
    headerClassName?: string;
    /** Optional className for group header rows */
    groupHeaderClassName?: string;
    /** Optional className for data rows */
    dataRowClassName?: string;
    /** Optional callback when a data row is clicked */
    onRowClick?: (item: TItem) => void;
    /** Optional callback when group expand/collapse state changes */
    onGroupToggle?: (groupId: string, isExpanded: boolean) => void;
}

export function GroupedList<TItem = any, TGroup = any>(props: GroupedListProps<TItem, TGroup>) {
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(props.groups.filter((g) => g.defaultExpanded !== false).map((g) => g.id)));

    const toggleGroup = (groupId: string) => {
        const newExpanded = new Set(expandedGroups);
        if (newExpanded.has(groupId)) {
            newExpanded.delete(groupId);
            props.onGroupToggle?.(groupId, false);
        } else {
            newExpanded.add(groupId);
            props.onGroupToggle?.(groupId, true);
        }
        setExpandedGroups(newExpanded);
    };

    const isExpanded = (groupId: string) => expandedGroups.has(groupId);

    return (
        <div className={`flex w-full flex-col ${props.className || ''}`}>
            {/* Column Headers */}
            <div
                className={`border-border-default text-foreground-default flex items-start gap-4 border-b text-base font-medium ${
                    props.headerClassName || ''
                }`}>
                {props.columns.map((column) => (
                    <div
                        key={column.key}
                        style={column.width && !column.width.startsWith('flex') ? { width: column.width } : undefined}
                        className={`${column.width?.startsWith('flex') ? column.width.replace('flex-', 'flex-[') + '_0_0]' : 'w-full px-4 py-3'} ${
                            column.className || ''
                        }`}>
                        {column.header}
                    </div>
                ))}
            </div>

            {/* Groups and Items */}
            {props.groups.map((group) => (
                <div key={group.id}>
                    {/* Group Header Row */}
                    <div
                        className={`border-border-default hover:bg-background-surface-hover flex h-11 cursor-pointer items-center justify-between border-b px-4 py-3 ${
                            props.groupHeaderClassName || ''
                        }`}
                        onClick={() => toggleGroup(group.id)}>
                        <div className='flex items-center gap-4'>
                            <div className='size-5 shrink-0'>{isExpanded(group.id) ? <LuChevronUp size={20} /> : <LuChevronDown size={20} />}</div>
                            <div className='text-foreground-default text-base font-semibold'>{props.renderGroupHeader(group.group)}</div>
                        </div>
                        {props.renderGroupActions && (
                            <div
                                onClick={(e) => e.stopPropagation()}
                                className='shrink-0'>
                                {props.renderGroupActions(group.group)}
                            </div>
                        )}
                    </div>

                    {/* Data Rows */}
                    {isExpanded(group.id) &&
                        group.items.map((item, index) => (
                            <div
                                key={index}
                                className={`border-border-default flex h-11 items-center gap-4 border-b ${
                                    props.onRowClick ? 'hover:bg-background-surface-hover cursor-pointer' : ''
                                } ${props.dataRowClassName || ''}`}
                                onClick={() => props.onRowClick?.(item)}>
                                {props.columns.map((column) => (
                                    <div
                                        key={column.key}
                                        style={column.width && !column.width.startsWith('flex') ? { width: column.width } : undefined}
                                        className={`flex items-center gap-2.5 ${
                                            column.width?.startsWith('flex') ? column.width.replace('flex-', 'flex-[') + '_0_0]' : 'w-full'
                                        } ${column.className || ''}`}>
                                        {column.render ? column.render(item) : column.accessor ? column.accessor(item) : (item as any)[column.key]}
                                    </div>
                                ))}
                            </div>
                        ))}
                </div>
            ))}
        </div>
    );
}
